import React, { useState, useEffect } from 'react';
import { Table, Badge, Space, message } from "antd";
import { Link } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from "feather-icons-react";
import { onShowSizeChange, itemRender } from '../Pagination';
import { visitApi } from '../../api/visit.api';
import { plusicon, refreshicon, searchnormal, imagesend } from '../imagepath';

const AllVisitsHistory = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [visits, setVisits] = useState([]);
    const [visitsLoading, setVisitsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: false // Remove the "Показване на..." text
    });

    // Load visits when pagination changes (includes initial load)
    useEffect(() => {
        loadVisits();
    }, [pagination.current, pagination.pageSize]);

    const loadVisits = async () => {
        try {
            setVisitsLoading(true);
            const response = await visitApi.getVisitsPaginated(
                pagination.current - 1, // API uses 0-based pagination
                pagination.pageSize,
                'createdAt',
                'desc' // latest first
            );
            
            setVisits(response.content);
            setPagination(prev => ({
                ...prev,
                total: response.totalElements
            }));
        } catch (error) {
            console.error('Failed to load visits:', error);
            message.error('Не успяхме да заредим списъка с посещения');
        } finally {
            setVisitsLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination({
            ...pagination,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    // Filter visits based on search term (client-side filtering for now)
    const filteredVisits = visits.filter(visit => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            visit.noAl?.toLowerCase().includes(searchLower) ||
            visit.patientName?.toLowerCase().includes(searchLower) ||
            visit.patientEgn?.toLowerCase().includes(searchLower) ||
            visit.doctorName?.toLowerCase().includes(searchLower) ||
            visit.doctorUinNumber?.toLowerCase().includes(searchLower)
        );
    });

    const getVisitTypeLabel = (visit) => {
        // Use the new normalized visit type field
        return visit.visitTypeName || "Стандартно посещение";
    };

    const getExamTypeLabel = (examType) => {
        switch (examType) {
            case 11: return "Първичен";
            case 12: return "Контролен";
            case 13: return "Профилактичен";
            default: return `Тип ${examType}`;
        }
    };

    const columns = [
        {
            title: "№ Амбулаторен лист",
            dataIndex: "noAl",
            width: "15%",
            className: "text-nowrap",
            render: (text, record) => (
                <Link to={`/visits/${record.id}`} className="text-decoration-none">
                    {text}
                </Link>
            ),
        },
        {
            title: "Пациент",
            dataIndex: "patientName",
            width: "25%",
            className: "text-nowrap",
            render: (text, record) => (
                <Link to={`/patients/medical-profile/${record.patientId}`} className="text-decoration-none text-sm">
                    <div>
                        <div>{record.patientName}</div>
                        <small className="text-muted">ЕГН: {record.patientEgn}</small>
                    </div>
                </Link>
            ),
        },
        {
            title: "Дата и час",
            dataIndex: "visitDate",
            width: "15%",
            className: "text-nowrap",
            render: (text, record) => (
                <div>
                    <div>{new Date(record.visitDate).toLocaleDateString('bg-BG')}</div>
                    <small className="text-muted">{record.visitTime}</small>
                </div>
            ),
        },
        {
            title: "Лекар",
            dataIndex: "doctorName",
            width: "25%",
            className: "text-nowrap",
            render: (text, record) => (
                <div>
                    <div className="text-sm">{record.doctorName}</div>
                    <small className="text-muted">
                        УИН: {record.doctorUinNumber}
                    </small>
                </div>
            ),
        },
        {
            title: "Тип посещение",
            width: "10%",
            className: "text-nowrap",
            render: (text, record) => (
                <Badge color="green">{getVisitTypeLabel(record)}</Badge>
            ),
        },
        {
            title: "Тип преглед",
            dataIndex: "examType",
            width: "10%",
            className: "text-nowrap",
            render: (examType) => (
                <Badge color="orange">{getExamTypeLabel(examType)}</Badge>
            ),
        },
        {
            title: "",
            dataIndex: "FIELD8",
            render: (text, record) => (
                <>
                    <div className="text-end">
                        <div className="dropdown dropdown-action">
                            <Link
                                to="#"
                                className="action-icon dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FeatherIcon icon="more-vertical" />
                            </Link>
                            <div className="dropdown-menu dropdown-menu-end">
                                <Link className="dropdown-item" to={`/visits/${record.id}`}>
                                    <i className="far fa-eye me-2" />
                                    Преглед
                                </Link>
                                <Link className="dropdown-item" to={`/visits/${record.id}/edit`}>
                                    <i className="far fa-edit me-2" />
                                    Редактирай
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )
        },
    ];

    return (
        <>
            <Header />
            <Sidebar id="menu-item4" id1="menu-items4" activeClassName="visits-history" />
            <div className="page-wrapper" style={{ overflowX: 'hidden' }}>
                <div className="content" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="#">Прегледи</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Списък прегледи</li>
                                </ul>
                            </div>
                        </div>
                    </div>


                    {/* Visits Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Table Header */}
                                    <div className="page-table-header mb-2">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="doctor-table-blk" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                                    <h3>Медицински посещения ({pagination.total})</h3>
                                                    <div className="doctor-search-blk" style={{ maxWidth: '100%' }}>
                                                        <div className="top-nav-search table-search-blk" style={{ maxWidth: '400px' }}>
                                                            <form>
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control" 
                                                                    placeholder="Търсене по № амбулаторен лист, пациент, диагноза, МКБ код..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                    style={{ maxWidth: '350px' }}
                                                                />
                                                                <Link className="btn">
                                                                    <img src={searchnormal} alt="Търсене" />
                                                                </Link>
                                                            </form>
                                                        </div>
                                                        <div className="add-group">
                                                            <Link 
                                                                to="/visits/new" 
                                                                className="btn btn-primary add-pluss ms-2"
                                                            >
                                                                <img src={plusicon} alt="Добави" />
                                                            </Link>
                                                            <button 
                                                                className="btn btn-primary doctor-refresh ms-2"
                                                                onClick={loadVisits}
                                                                disabled={visitsLoading}
                                                            >
                                                                <img src={refreshicon} alt="Обнови" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div 
                                        className="table-responsive doctor-list" 
                                        style={{ 
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            WebkitOverflowScrolling: 'touch',
                                            scrollbarWidth: 'thin',
                                            msOverflowStyle: 'auto'
                                        }}
                                    >
                                        <Table
                                            pagination={{
                                                ...pagination,
                                                onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                                                onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size }),
                                                itemRender: itemRender,
                                                style: { paddingRight: '30px' }
                                            }}
                                            columns={columns}
                                            dataSource={filteredVisits}
                                            rowSelection={rowSelection}
                                            rowKey={(record) => record.id}
                                            loading={visitsLoading}
                                            showSorterTooltip={false}
                                            style={{
                                                width: '100%',
                                                tableLayout: 'fixed'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                /* Force pagination right alignment */
                .ant-table-pagination-right {
                    text-align: right !important;
                    display: flex !important;
                    justify-content: flex-end !important;
                    padding-right: 20px !important;
                }
                
                /* Mobile specific left alignment */
                @media (max-width: 768px) {
                    .ant-table-pagination-right,
                    .ant-pagination {
                        text-align: left !important;
                        display: flex !important;
                        justify-content: flex-start !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding-left: 20px !important;
                    }
                }
            `}</style>

            {/* Delete Visit Modal */}
            <div
                id="delete_visit"
                className="modal fade delete-modal"
                role="dialog"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body text-center">
                            <img src={imagesend} alt="" width={50} height={46} />
                            <h3>Сигурни ли сте, че искате да изтриете това посещение?</h3>
                            <div className="m-t-20">
                                <Link to="#" className="btn btn-white" data-bs-dismiss="modal">
                                    Затвори
                                </Link>
                                <button type="submit" className="btn btn-danger">
                                    Изтрий
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllVisitsHistory;